import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

interface AdultCountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCount: (count: number) => void;
  selectedCount: number;
  enableChildren?: boolean;
  selectedChildrenCount?: number;
  onSelectChildrenCount?: (count: number) => void;
  maxTotalCount?: number;
  asInline?: boolean;
}

export function AdultCountModal({
  isOpen,
  onClose,
  onSelectCount,
  selectedCount,
  enableChildren = false,
  selectedChildrenCount = 0,
  onSelectChildrenCount,
  maxTotalCount,
  asInline = false,
}: AdultCountModalProps) {
  const { t } = useTranslation();
  const maxParticipants = typeof maxTotalCount === 'number' && maxTotalCount > 0 ? maxTotalCount : Infinity;
  const canIncreaseTotal = selectedCount + selectedChildrenCount < maxParticipants;

  const handleDecrementAdults = useCallback(() => {
    onSelectCount(Math.max(1, selectedCount - 1));
  }, [onSelectCount, selectedCount]);

  const handleIncrementAdults = useCallback(() => {
    if (!canIncreaseTotal) return;
    onSelectCount(selectedCount + 1);
  }, [canIncreaseTotal, onSelectCount, selectedCount]);

  const handleDecrementChildren = useCallback(() => {
    onSelectChildrenCount?.(Math.max(0, selectedChildrenCount - 1));
  }, [onSelectChildrenCount, selectedChildrenCount]);

  const handleIncrementChildren = useCallback(() => {
    if (!canIncreaseTotal) return;
    onSelectChildrenCount?.(selectedChildrenCount + 1);
  }, [canIncreaseTotal, onSelectChildrenCount, selectedChildrenCount]);

  const content = (
    <div className="flex flex-col gap-0.5 -mx-2">
      <div className="flex items-center justify-between rounded-xl px-3 py-3 transition-all">
        <div className="min-w-0 flex-1 text-left">
          <p className="nav-label text-gray-900">{t('common.adults', 'Adults')}</p>
          <p className="desc-sm !text-gray-400">12+ years</p>
        </div>
        <div className="flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={handleDecrementAdults}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-white hover:text-gray-900 transition-all active:scale-95 shadow-sm"
          >
            <span className="text-xl leading-none font-medium mb-[2px]">−</span>
          </button>
          <span className="w-5 text-center stat-sm">{selectedCount}</span>
          <button
            type="button"
            onClick={handleIncrementAdults}
            disabled={!canIncreaseTotal}
            className={`flex h-8 w-8 items-center justify-center rounded-full border transition-all active:scale-95 shadow-sm ${
              canIncreaseTotal
                ? 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-white hover:text-gray-900'
                : 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300 active:scale-100 shadow-none'
            }`}
          >
            <span className="text-xl leading-none font-medium mb-[2px]">+</span>
          </button>
        </div>
      </div>

      {enableChildren ? (
        <div className="flex items-center justify-between rounded-xl px-3 py-3 transition-all">
          <div className="min-w-0 flex-1 text-left">
            <p className="nav-label text-gray-900">{t('common.children', 'Children')}</p>
            <p className="text-[1px] text-gray-400">0-11 years</p>
          </div>
          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={handleDecrementChildren}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-white hover:text-gray-900 transition-all active:scale-95 shadow-sm"
            >
              <span className="text-xl leading-none font-medium mb-[2px]">−</span>
            </button>
            <span className="w-5 text-center stat-sm">{selectedChildrenCount}</span>
            <button
              type="button"
              onClick={handleIncrementChildren}
              disabled={!canIncreaseTotal}
              className={`flex h-8 w-8 items-center justify-center rounded-full border transition-all active:scale-95 shadow-sm ${
                canIncreaseTotal
                  ? 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-white hover:text-gray-900'
                  : 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300 active:scale-100 shadow-none'
              }`}
            >
              <span className="text-xl leading-none font-medium mb-[2px]">+</span>
            </button>
          </div>
        </div>
      ) : null}

      <div className="my-1.5 border-t border-gray-100" />

      <div className="px-3 py-1.5 text-center">
        <p className="desc-sm !font-bold !text-gray-600">
          {t('common.total', 'Total')}:{' '}
          <span className="font-black text-gray-900">
            {selectedCount + (enableChildren ? selectedChildrenCount : 0)}
          </span>
        </p>
        {Number.isFinite(maxParticipants) && maxParticipants !== Infinity ? (
          <p className="mt-1 eyebrow text-gray-400">Maximum {maxParticipants} spots</p>
        ) : null}
      </div>

      {asInline && (
        <div className="mt-3 px-3 pb-1">
          <Button type="button" onClick={onClose} fullWidth title={t('common.done', 'Done')} />
        </div>
      )}
    </div>
  );

  if (asInline) return content;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('common.participants', 'Participants')}
      size="sm"
      footer={
        <div className="flex gap-3">
          <Button type="button" onClick={onClose} fullWidth title={t('common.done', 'Done')} />
        </div>
      }
    >
      {content}
    </Modal>
  );
}
